import React, { memo, Fragment, useContext, useState } from 'react'
import { AuthContext } from '../context/auth-context'
import { SettingsContext } from '../context/settings-context'
import { useForm } from '../utils/form-hook'
import { useDotsAnimation } from '../utils/dot-animation-hook'
import { gql, useMutation } from '@apollo/client'
import { Row, Col, FormCheck, Form, Button, Alert, Spinner } from 'react-bootstrap'
import Card from '../components/common/Card'
import LogoDark from '../assets/images/logo-text-dark.png'
import LogoLight from '../assets/images/logo-text-light.png'
import { Link, useNavigate } from 'react-router-dom'
import Notification from '../components/partials/Notification'
import PasswordInput from '../components/common/PasswordInput'

const LOGIN_USER = gql`
    mutation Login($loginInput: LoginInput) {
        login(loginInput: $loginInput) {
            token
        }
    }
`

const Login = memo(() => {
    const { theme } = useContext(SettingsContext)
    const context = useContext(AuthContext)
    const navigate = useNavigate()
    const [errors, setError] = useState([])

    const loginUserCallback = (event) => {
        loginUser()
    }

    const { onChange, onSubmit, values, validated } = useForm(loginUserCallback, {
        username: '',
        password: '',
        rememberMe: false,
        context: 'dashboard',
    })

    const { animationRef } = useDotsAnimation()

    const [loginUser, { loading }] = useMutation(LOGIN_USER, {
        update(proxy, { data: { login: userData } }) {
            context.login(userData)
            navigate('/')
        },
        onError({ graphQLErrors }) {
            setError(graphQLErrors)
        },
        variables: { loginInput: { ...values, rememberMe: values.rememberMe === 'true' } },
    })

    return (
        <Fragment>
            <section className="iq-auth-page" ref={animationRef}>
                <Row className="d-flex align-items-center justify-content-center vh-100 w-100">
                    <Col xs="10" sm="8" md="6" lg="4" xl="3" style={{ minWidth: '400px' }}>
                        <Card className="m-4 p-3">
                            <Card.Body>
                                <h4 className="text-center mb-4">Dashboard Sign In</h4>
                                <div className="d-flex justify-content-center mb-5">
                                    <img
                                        src={theme === 'dark' ? LogoDark : LogoLight}
                                        alt="Turba Media"
                                        style={{ height: '45px' }}
                                    />
                                </div>
                                <Form noValidate validated={validated}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Username</Form.Label>
                                        <Form.Control
                                            required
                                            type="username"
                                            className="mb-0"
                                            placeholder="Enter username"
                                            name="username"
                                            onChange={onChange}
                                        ></Form.Control>
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Password</Form.Label>
                                        <PasswordInput
                                            required
                                            className="mb-0"
                                            placeholder="Enter password"
                                            name="password"
                                            onChange={onChange}
                                        />
                                    </Form.Group>
                                    <div className="d-flex justify-content-between mb-3">
                                        <Form.Check className="d-flex align-items-center m-0">
                                            <FormCheck.Input
                                                onChange={(event) => {
                                                    event.target.value = event.target.checked
                                                    return onChange(event)
                                                }}
                                                type="checkbox"
                                                id="rememberMe"
                                                name="rememberMe"
                                            ></FormCheck.Input>
                                            <Form.Check.Label htmlFor="rememberMe" className="mb-0">
                                                Remember Me
                                            </Form.Check.Label>
                                        </Form.Check>
                                        {/*<Link to="/auth/reset-password">Forget password</Link>*/}
                                    </div>
                                    <div className="">
                                        {errors.map((error, key) => {
                                            return (
                                                <Alert
                                                    key={key}
                                                    variant="warning rounded-0 alert-dismissible my-3 p-2 fade show "
                                                    role="alert"
                                                >
                                                    <span> {error.message} </span>
                                                </Alert>
                                            )
                                        })}
                                    </div>
                                    <div className="text-center mb-4 d-grid">
                                        <Button
                                            type="button"
                                            variant="btn btn-primary"
                                            onClick={onSubmit}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <Fragment>
                                                    <Spinner
                                                        as="span"
                                                        animation="grow"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />{' '}
                                                    Signing in...
                                                </Fragment>
                                            ) : (
                                                <Fragment>Sign in</Fragment>
                                            )}
                                        </Button>
                                    </div>
                                </Form>

                                <hr className="mb-4 w-100 border-0 border-top border-primary" />

                                <p className="text-center mb-0">
                                    <Link to="https://admin.turbamedia.io">Switch to Admin</Link>
                                </p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </section>
            <Notification />
        </Fragment>
    )
})

Login.displayName = 'Login'
export default Login
